import { signal, WritableSignal, computed, Signal } from '@angular/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { BaseAssetsStoreService } from '../assets-manager/assets-stores/base-assets-store.service';

export interface PaginatedAsset<TAsset, TMetadata> {
  data: TAsset;
  metadata?: TMetadata | null;
  isLoadingMetadata?: boolean;
  metadataError?: string;
}

interface MetadataPaginationState {
  visibleCount: number;
  pageSize: number;
  totalItems: number;
  hasMore: boolean;
}

export abstract class BaseAssetMetadataService<TAsset, TMetadata> {
  // Signals for reactive state
  protected allAssets: TAsset[] = [];
  protected paginatedAssetsSignal: WritableSignal<PaginatedAsset<TAsset, TMetadata>[]> = signal([]);
  protected paginationStateSignal: WritableSignal<MetadataPaginationState> = signal({
    visibleCount: 0,
    pageSize: 20,
    totalItems: 0,
    hasMore: true
  });
  protected isLoadingSignal: WritableSignal<boolean> = signal(false);

  // Subjects for scroll handling
  protected scrollSubject = new Subject<number>();

  // Public readable signals
  public readonly paginatedAssets: Signal<PaginatedAsset<TAsset, TMetadata>[]> = this.paginatedAssetsSignal.asReadonly();
  public readonly paginationState: Signal<MetadataPaginationState> = this.paginationStateSignal.asReadonly();
  public readonly isLoading: Signal<boolean> = this.isLoadingSignal.asReadonly();

  // Computed signals
  public readonly displayedItemsCount = computed(() => this.paginatedAssetsSignal().length);
  public readonly hasMoreItems = computed(() => this.paginationStateSignal().hasMore);

  constructor(protected readonly assetsStoreService: BaseAssetsStoreService<any>, protected readonly assetStoreKey: string) {
    this.initializeScrollHandling();
  }

  private initializeScrollHandling(): void {
    // Debounce scroll events to prevent excessive loading
    this.scrollSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(scrollPercentage => {
      if (scrollPercentage >= 70 && !this.isLoadingSignal() && this.hasMoreItems()) {
        this.loadMore();
      }
    });
  }

  /**
   * Initialize the service with assets from the store
   */
  public initialize(assets?: TAsset[]): void {
    // Use provided assets or get from store
    this.allAssets = assets || this.getAssetsFromStore();

    // Update pagination state
    this.paginationStateSignal.update(state => ({
      ...state,
      totalItems: this.allAssets.length,
      hasMore: this.allAssets.length > 0 // Reset hasMore flag based on available assets
    }));

    // Reset pagination state and load initial page
    // This ensures stale data from previous wallet is cleared
    this.reset();

    // Only load more if we have assets to display
    if (this.allAssets.length > 0) {
      this.loadMore();
    }
  }
  /**
   * Load metadata for a specific asset
   */
  protected abstract loadMetadata(asset: TAsset): Promise<TMetadata | null>;

  /**
   * Get unique identifier for an asset
   */
  protected abstract getAssetId(asset: TAsset): string;

  /**
   * Reset pagination and reload
   */
  public reset(): void {
    this.paginatedAssetsSignal.set([]);
    this.paginationStateSignal.update(state => ({
      ...state,
      visibleCount: 0,
      hasMore: true
    }));
  }

  /**
   * Update assets without resetting cached metadata
   * Preserves metadata for existing assets, only resets pagination state
   */
  public updateAssets(assets: TAsset[]): void {
    const previousPaginated = this.paginatedAssetsSignal();
    
    // Create a map of existing cached metadata by asset ID
    const metadataCache = new Map<string, { metadata?: TMetadata | null; isLoadingMetadata?: boolean; metadataError?: string }>();
    previousPaginated.forEach(item => {
      const id = this.getAssetId(item.data);
      if (item.metadata || item.isLoadingMetadata) {
        metadataCache.set(id, {
          metadata: item.metadata,
          isLoadingMetadata: item.isLoadingMetadata,
          metadataError: item.metadataError
        });
      }
    });
    
    // Update all assets
    this.allAssets = assets;
    
    // Update pagination state
    let latestVisibleCount = 0;
    this.paginationStateSignal.update(state => {
      const clampedVisible = Math.min(state.visibleCount, this.allAssets.length);
      latestVisibleCount = clampedVisible;
      return {
        ...state,
        visibleCount: clampedVisible,
        totalItems: this.allAssets.length,
        hasMore: clampedVisible < this.allAssets.length
      };
    });

    const visibleCount = latestVisibleCount;
    const visibleAssets = assets.slice(0, visibleCount);
    
    const rebuiltPaginated: PaginatedAsset<TAsset, TMetadata>[] = visibleAssets.map(asset => {
      const id = this.getAssetId(asset);
      const cached = metadataCache.get(id);
      
      return {
        data: asset,
        metadata: cached?.metadata,
        isLoadingMetadata: cached?.isLoadingMetadata || false,
        metadataError: cached?.metadataError
      };
    });
    
    this.paginatedAssetsSignal.set(rebuiltPaginated);
  }

  /**
   * Load more items with pagination
   */
  public async loadMore(): Promise<void> {
    if (this.isLoadingSignal() || !this.hasMoreItems()) {
      return;
    }

    this.isLoadingSignal.set(true);

    try {
      const state = this.paginationStateSignal();
      const startIndex = state.visibleCount;

      // Get next batch of assets
      const assets = this.getAssetsFromStore();
      this.allAssets = assets;
      const endIndex = Math.min(startIndex + state.pageSize, assets.length);
      const nextBatch = assets.slice(startIndex, endIndex);

      if (nextBatch.length === 0) {
        this.paginationStateSignal.update(s => ({
          ...s,
          hasMore: false,
          visibleCount: Math.min(s.visibleCount, assets.length)
        }));
        return;
      }

      // Create paginated assets without metadata
      const paginatedBatch: PaginatedAsset<TAsset, TMetadata>[] = nextBatch.map(asset => ({
        data: asset,
        metadata: undefined,
        isLoadingMetadata: false
      }));

      // Add to displayed items
      this.paginatedAssetsSignal.update(items => [...items, ...paginatedBatch]);

      // Update pagination state
      this.paginationStateSignal.update(s => ({
        ...s,
        visibleCount: endIndex,
        hasMore: endIndex < assets.length,
        totalItems: assets.length
      }));

      // Load metadata for visible items (defer to next tick)
      setTimeout(() => {
        this.loadMetadataForVisibleItems();
      }, 0);

    } finally {
      this.isLoadingSignal.set(false);
    }
  }

  /**
   * Load metadata for items that are visible in the viewport
   */
  public async loadMetadataForVisibleItems(itemElements?: HTMLElement[]): Promise<void> {
    const items = this.paginatedAssetsSignal();
    const itemsToLoad = items.filter(item =>
      !item.metadata && !item.isLoadingMetadata && !item.metadataError
    );

    // If itemElements are provided, only load metadata for visible ones
    if (itemElements && itemElements.length > 0) {
      const visibleIndices = this.getVisibleItemIndices(itemElements);
      const visibleItemsToLoad = itemsToLoad.filter((_, index) => visibleIndices.includes(index));
      await this.loadMetadataForItems(visibleItemsToLoad);
    } else {
      // Load metadata for all items without metadata
      await this.loadMetadataForItems(itemsToLoad);
    }
  }

  private getVisibleItemIndices(itemElements: HTMLElement[]): number[] {
    const viewportHeight = window.innerHeight;
    const indices: number[] = [];

    itemElements.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      if (rect.top < viewportHeight && rect.bottom > 0) {
        indices.push(index);
      }
    });

    return indices;
  }

  private async loadMetadataForItems(items: PaginatedAsset<TAsset, TMetadata>[]): Promise<void> {
    const promises = items.map(async (item) => {
      const assetId = this.getAssetId(item.data);

      // Mark as loading
      this.updateItemMetadataState(assetId, { isLoadingMetadata: true });

      try {
        const metadata = await this.loadMetadata(item.data);
        this.updateItemMetadataState(assetId, {
          metadata,
          isLoadingMetadata: false,
          metadataError: undefined
        });
      } catch (error) {
        console.error(`Failed to load metadata for asset ${assetId}:`, error);
        this.updateItemMetadataState(assetId, {
          isLoadingMetadata: false,
          metadataError: 'Failed to load metadata'
        });
      }
    });

    await Promise.all(promises);
  }

  private updateItemMetadataState(assetId: string, updates: Partial<PaginatedAsset<TAsset, TMetadata>>): void {
    this.paginatedAssetsSignal.update(items =>
      items.map(item =>
        this.getAssetId(item.data) === assetId
          ? { ...item, ...updates }
          : item
      )
    );
  }

  /**
   * Handle scroll event
   */
  public onScroll(scrollPercentage: number): void {
    this.scrollSubject.next(scrollPercentage);
  }

  /**
   * Get assets from store - to be implemented by subclasses
   */
  protected getAssetsFromStore(): TAsset[] {
    return this.allAssets && this.allAssets.length > 0 ? this.allAssets : this.assetsStoreService.getAssetSignal(this.assetStoreKey)() || [];
  }

  /**
 * Refresh data from store and reinitialize
 */
  public refreshFromStore(): void {
    this.allAssets = this.assetsStoreService.getAssetSignal(this.assetStoreKey)() || [];
    this.initialize(this.allAssets);
  }
} 